import { useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router';

import { OpenAppParams } from 'app/shared/types/open-app-params.types';

const LOCAL_STORAGE_KEY = 'openAppParams';

interface QueryParams {
  authToken: string;
  apiUrl: string;
  appBrand: string;
  locale: string;
  userBuildingUuid: string;
}

interface PersistedParams {
  authToken?: string;
  apiUrl?: string;
  appBrand?: string;
  locale?: string;
  userBuildingUuid?: string;
  /** Legacy field name for back-compat with localStorage written by older builds. */
  buildingUuid?: string;
}

const readPersisted = (): PersistedParams => {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as PersistedParams) : {};
  } catch {
    return {};
  }
};

const writePersisted = (params: QueryParams): void => {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(params));
  } catch {
    // ignore quota errors — running without persistence is acceptable
  }
};

/**
 * Pure resolver: prefers the value from the URL, falls back to localStorage,
 * then empty string. Avoids the old "all-or-nothing" trap where a single
 * missing param would drop the entire payload. No side effects — the
 * write-back happens in a useEffect inside the hook.
 */
const resolveQueryParams = (search: URLSearchParams, persisted: PersistedParams): QueryParams => {
  const pick = (key: keyof QueryParams, legacyKey?: keyof PersistedParams): string =>
    search.get(key)
    ?? persisted[key]
    ?? (legacyKey ? persisted[legacyKey] : undefined)
    ?? '';

  return {
    authToken: pick('authToken'),
    apiUrl: pick('apiUrl'),
    appBrand: pick('appBrand'),
    locale: pick('locale') || 'en',
    userBuildingUuid: search.get('buildingUuid') ?? persisted.userBuildingUuid ?? persisted.buildingUuid ?? '',
  };
};

export const useOpenParams = (): OpenAppParams => {
  const [searchParams] = useSearchParams();
  const pathParams = useParams<{
    providerUuid?: string;
    buildingUuid?: string;
    serviceUuid?: string;
    timeSlotUuid?: string;
  }>();

  const params = useMemo<OpenAppParams>(() => {
    const query = resolveQueryParams(searchParams, readPersisted());
    return {
      ...query,
      providerUuid: pathParams.providerUuid,
      serviceBuildingUuid: pathParams.buildingUuid,
      serviceUuid: pathParams.serviceUuid,
      timeSlotUuid: pathParams.timeSlotUuid,
    };
  }, [searchParams, pathParams.providerUuid, pathParams.buildingUuid, pathParams.serviceUuid, pathParams.timeSlotUuid]);

  // Persist fresh query params so a subsequent reload (which may have
  // stripped the query string) can recover them. Side effect lives here
  // rather than in `resolveQueryParams` — React 18 StrictMode and React's
  // own purity contract for `useMemo` disallow writes inside the memo.
  useEffect(() => {
    if (searchParams.size === 0) return;
    writePersisted({
      authToken: params.authToken,
      apiUrl: params.apiUrl,
      appBrand: params.appBrand,
      locale: params.locale,
      userBuildingUuid: params.userBuildingUuid,
    });
  }, [
    searchParams,
    params.authToken,
    params.apiUrl,
    params.appBrand,
    params.locale,
    params.userBuildingUuid,
  ]);

  return params;
};
